import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import { Tooltip } from "@mui/material";
import { ReactNode } from "react";
import { FieldValues } from "react-hook-form";

type InputTooltipProps<T extends FieldValues> = {
  title: ReactNode;
  /**
   * Color for the "?" icon. Omitted means inherit, which is what every
   * customer-facing field wants; staff-only controls pass the theme's
   * reserved staff accent so they read as staff at a glance.
   */
  iconColor?: string;
  placement?:
    | "bottom"
    | "bottom-end"
    | "bottom-start"
    | "left"
    | "left-end"
    | "left-start"
    | "right"
    | "right-end"
    | "right-start"
    | "top"
    | "top-end"
    | "top-start";
};

export const InputTooltip = <T extends FieldValues>({
  title,
  iconColor,
  placement = "bottom",
}: InputTooltipProps<T>) => {
  return (
    <Tooltip
      title={title}
      className="input-tooltip"
      arrow
      // Opt out of the app-wide non-interactive Tooltip default so the 250ms
      // leaveDelay can keep this help tooltip alive while the user mouses from
      // the "?" icon onto the tooltip text to read it.
      disableInteractive={false}
      leaveDelay={250}
      style={{ marginLeft: "4px", padding: "0" }}
      placement={placement}
      slotProps={{
        tooltip: {
          sx: (theme) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightRegular,
            padding: theme.spacing(1),
            boxShadow: theme.shadows[5],
            whiteSpace: "normal",
          }),
        },
        arrow: {
          sx: (theme) => ({
            color: theme.palette.background.paper,
          }),
        },
      }}
    >
      <HelpOutlineOutlined fontSize="small" sx={{ color: iconColor }} />
    </Tooltip>
  );
};
